import React from "react";
import { useNavigate } from "react-router-dom";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import HomeIcon from "@mui/icons-material/Home";
import { Drawer, Typography, Divider, Box, ListSubheader } from "@mui/material";
import { TbReport } from "react-icons/tb";
import { GrSchedulePlay } from "react-icons/gr";
import { FaMapMarkedAlt, FaRobot } from "react-icons/fa";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import SupervisedUserCircleIcon from "@mui/icons-material/SupervisedUserCircle";
import DomainIcon from "@mui/icons-material/Domain";
import SmartToyIcon from "@mui/icons-material/SmartToy";

import { hasWarningStatus, isNotCharging } from "../../helpers/robotHelper";
import { useAppNavigate } from "../../routes/useAppNavigate";
import { useLoginUser } from "../../hooks/useLoginUser";
import { useDeviceType } from "../../hooks/useDeviceType";
import { useListRobotQuery } from "hooks/worker/useRobot";
import { useListCleaningEventByDateQuery } from "hooks/worker/useCleaningEvent";
import { hasWarningConsumablePart } from "lib/robot";

const SideMenu: React.FC = () => {
  const { isSystemAdmin, isTenantAdmin, isAssignedToBuilding } = useLoginUser();
  const { isMobile } = useDeviceType();
  const { systemAdminNavigate, systemOrTenantAdminNavigate } = useAppNavigate();
  const { data: robots = [] } = useListRobotQuery({
    enabled: isAssignedToBuilding,
  });

  const navigate = useNavigate();
  const { data: logs } = useListCleaningEventByDateQuery({
    enabled: isAssignedToBuilding,
  });

  const onClickHome = () => {
    navigate("/");
  };

  const onClickCleaning = () => {
    navigate(`/CleaningEvent`);
  };

  const onClickSchedule = () => {
    navigate(`/ScheduleList`);
  };

  const onClickRobotStatus = () => {
    navigate(`/RobotStatus`);
  };

  const onClickMap = () => {
    navigate(`/MapSchedule`);
  };

  const hasWarningRobotStatus =
    logs &&
    logs.cleaningEvents
      .filter((l) => robots.some((r) => r.robotId === l.robotId))
      .some(hasWarningStatus);
  const hasWarningRobotParts = robots.some((r) =>
    r.consumableParts.some(hasWarningConsumablePart)
  );

  const hasNotChargingRobot = robots.some(isNotCharging);

  if (isMobile) {
    //画面サイズがスマホの場合サイドメニューを表示しない
    return <Drawer variant="temporary"></Drawer>;
  } else {
    //画面サイズがスマホ以外の場合サイドメニューを表示する
    return (
      <Box display={"flex"} flexDirection={"column"} sx={{ height: "100%" }}>
        <List component="nav" sx={{ minWidth: "280px", marginTop: "55px" }}>
          <ListItemButton onClick={onClickHome}>
            <ListItemIcon>
              <HomeIcon sx={{ fontSize: "25" }} />
            </ListItemIcon>
            <ListItemText primary="ホーム" />
          </ListItemButton>
          <ListItemButton onClick={onClickCleaning}>
            <ListItemIcon>
              <TbReport size={25} />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography component="span" variant="body1">
                  <span style={{ display: "flex", alignItems: "center" }}>
                    清掃結果
                    <ReportProblemIcon
                      color="error"
                      style={{ marginLeft: "0.5rem" }}
                      sx={{ opacity: hasWarningRobotStatus ? 1 : 0 }} // アイコン用の幅を確保するために、透過で表示・非表示を制御する
                    />
                  </span>
                </Typography>
              }
            />
          </ListItemButton>
          <ListItemButton onClick={onClickSchedule}>
            <ListItemIcon>
              <GrSchedulePlay size={25} />
            </ListItemIcon>
            <ListItemText primary="次回清掃予定" />
          </ListItemButton>
          <ListItemButton onClick={onClickRobotStatus}>
            <ListItemIcon>
              <FaRobot size={25} />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography component="span" variant="body1">
                  <span style={{ display: "flex", alignItems: "center" }}>
                    ロボット一覧{" "}
                    <ReportProblemIcon
                      color="error"
                      style={{ marginLeft: "0.5rem" }}
                      sx={{
                        opacity:
                          hasWarningRobotParts || hasNotChargingRobot ? 1 : 0,
                      }} // アイコン用の幅を確保するために、透過で表示・非表示を制御する
                    />
                  </span>
                </Typography>
              }
            />
          </ListItemButton>
          <ListItemButton onClick={onClickMap}>
            <ListItemIcon>
              <FaMapMarkedAlt size={25} />
            </ListItemIcon>
            <ListItemText primary="マップ一覧" />
          </ListItemButton>
        </List>
        {isSystemAdmin && (
          <>
            <Divider />
            <List
              subheader={
                <ListSubheader sx={{ bgcolor: "transparent" }}>
                  システム管理者メニュー
                </ListSubheader>
              }
            >
              <ListItemButton
                onClick={() => {
                  systemAdminNavigate.listTenants();
                }}
              >
                <ListItemIcon>
                  <DomainIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="テナント管理" />
              </ListItemButton>
              <ListItemButton
                onClick={() => {
                  systemAdminNavigate.listRobot();
                }}
              >
                <ListItemIcon>
                  <SmartToyIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="全ロボット一覧" />
              </ListItemButton>
              <ListItemButton
                onClick={() => {
                  systemAdminNavigate.listSystemAdmin();
                }}
              >
                <ListItemIcon>
                  <SupervisedUserCircleIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="システム管理者" />
              </ListItemButton>
            </List>
          </>
        )}
        {isTenantAdmin && (
          <>
            <Divider />
            <List
              subheader={
                <ListSubheader sx={{ bgcolor: "transparent" }}>
                  テナント管理者メニュー
                </ListSubheader>
              }
            >
              <ListItemButton
                onClick={() => {
                  systemOrTenantAdminNavigate.detailTenant();
                }}
              >
                <ListItemIcon>
                  <DomainIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="テナント管理" />
              </ListItemButton>
              <ListItemButton
                onClick={() => {
                  systemOrTenantAdminNavigate.listTenantRobot();
                }}
              >
                <ListItemIcon>
                  <SmartToyIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="ロボット管理" />
              </ListItemButton>
              <ListItemButton
                onClick={() => {
                  systemOrTenantAdminNavigate.listTenantUser();
                }}
              >
                <ListItemIcon>
                  <SupervisedUserCircleIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="ユーザー一覧" />
              </ListItemButton>
            </List>
          </>
        )}
      </Box>
    );
  }
};
export default SideMenu;
